import type { FileRef } from "@bundle/contracts";
import { Files, Shell, type RichTextContext, type TimelineItem } from "@bundle/core";
import {
  Composer,
  DayDivider,
  BroadcastConfirmDialog,
  DeleteMessageDialog,
  Divider,
  DropZone,
  Icon,
  IconButton,
  List,
  MessageRow,
  BlockedComposer,
  BlockedMessageRow,
  NewDivider,
  Stack,
  Text,
  Toast,
  ThreadSummaryCard,
  TypingIndicator,
  useChannel,
  useThreadSummary,
  useFileActions,
  useNavigation,
  useGroupCard,
  useT,
  useTr,
  useWorkspace,
  FEATURE_COPY,
  useFeatureAccess,
  MediaPicker,
  useMediaPicker,
  useStickers,
} from "@bundle/ui";
import { BillingComposerNotice, ScheduledBar, useScheduleMenu, CommandNoticeToast, CommandRunningRow, EphemeralRow, anchorEphemerals, useBilling } from "@bundle/ui";
import { useEphemeralScroll } from "./useEphemeralScroll.ts";
import { useEffect, useMemo, useState, type ReactNode } from "react";
import { useMessageExtras } from "./messageExtras.ts";

export type ThreadScreenProps = { channelId: string; rootId: string };

/** A thread (usually in a split pane next to its channel): root, replies and a reply composer. */
export function ThreadScreen({ channelId, rootId }: ThreadScreenProps) {
  const t = useT();
  // Chat switched off while owners and admins may still look (Settings → Features): no replies.
  const access = useFeatureAccess();
  const billing = useBilling();
  const tr = useTr();
  const nav = useNavigation();
  const groupCard = useGroupCard();
  const ws = useWorkspace();
  const th = useChannel(channelId, rootId);
  // AI TL;DR under the root (hidden when the server or the workspace has summaries off).
  const summary = useThreadSummary(channelId, rootId);
  const baseFileActions = useFileActions(th?.state.caps.downloadFiles ?? false);
  // Stickers & GIFs (only where the shell provides them): the Emoji · Stickers · GIFs picker, "Save as sticker".
  const stickers = useStickers();
  const picker = useMediaPicker(channelId, rootId);
  const saveAsSticker = stickers?.state.canCreate === true ? stickers.creator : undefined;
  const fileActions =
    saveAsSticker === undefined ? baseFileActions : { ...baseFileActions, onSaveAsSticker: (f: FileRef) => void saveAsSticker.openFromUrl(f.url, f.name, f.mime) };
  const [scrollTo, setScrollTo] = useState<{ key: string; seq: number } | undefined>(undefined);
  useEphemeralScroll(th?.state.items, th?.state.ephemerals, (key) => setScrollTo((cur) => ({ key, seq: (cur?.seq ?? 0) + 1 })));
  const cache = ws.state.cache;
  const extrasFor = useMessageExtras(th?.vm, cache);
  // "Schedule message" in the send menu (and its custom-time sheet).
  const schedule = useScheduleMenu(th?.vm, "thread-composer");
  const ctx = useMemo<RichTextContext>(
    () => ({ meId: ws.state.meId, members: cache?.members ?? {}, channels: cache?.channels ?? {}, userGroups: cache?.userGroups ?? {} }),
    [ws.state.meId, cache?.members, cache?.channels, cache?.userGroups],
  );
  // Stickers and GIFs picked here follow the "Also send to #channel" checkbox (and clear it), like text replies.
  const pickerVm = picker?.vm;
  const threadVm = th?.vm;
  useEffect(() => {
    if (pickerVm === undefined || threadVm === undefined) return undefined;
    pickerVm.setAlsoSendSource(() => threadVm.takeAlsoSend());
    return () => pickerVm.setAlsoSendSource(undefined);
  }, [pickerVm, threadVm]);
  if (th === undefined || cache === undefined) return <Stack grow testID="thread-screen-loading" />;
  const s = th.state;

  const ephemeralsAt = anchorEphemerals(s.items, s.ephemerals);
  const openLink = (url: string) => window.open(url, "_blank", "noopener,noreferrer");
  const renderItem = (item: TimelineItem) => {
    if (item.type === "day") return <DayDivider label={item.label} />;
    if (item.type === "new") return <NewDivider />;
    const m = item.message;
    // From someone I blocked: one muted row until Show (logic/moderation).
    if (s.collapsedIds?.includes(m.id) === true) return <BlockedMessageRow onShow={() => th.vm.showBlocked(m.id)} testID={`thread-blocked-${m.id}`} />;
    const ephemerals = ephemeralsAt.get(item.key);
    const withEphemerals = (node: ReactNode) =>
      ephemerals === undefined ? node : (
        <Stack>
          {node}
          {ephemerals.map((e) => (
            <EphemeralRow
              key={e.id}
              message={e}
              ctx={ctx}
              onDismiss={() => th.vm.dismissEphemeral(e.id)}
              onButton={(blockId, button, values) => th.vm.pressButton(e.id, blockId, button, openLink, values, e.appBlocks)}
              onElement={(blockId, el, value, values) => th.vm.changeElement(e.id, blockId, el, value, values, e.appBlocks ?? [])}
              testID={`ephemeral-${e.id}`}
            />
          ))}
        </Stack>
      );
    const row = (
      <MessageRow
        onKitButton={(blockId, button, values) => th.vm.pressButton(m.id, blockId, button, openLink, values, m.appBlocks)}
        onKitElement={(blockId, el, value, values) => th.vm.changeElement(m.id, blockId, el, value, values, m.appBlocks ?? [])}
        item={item}
        author={cache.members[m.authorId]}
        meId={ws.state.meId}
        ctx={ctx}
        showThreadSummary={false}
        offline={s.offline}
        onToggleReaction={(emoji) => th.vm.toggleReaction(m.id, emoji)}
        onRetry={() => th.vm.retry(item.key)}
        onDiscard={() => th.vm.discard(item.key)}
        onUserPress={(userId) => nav.open(Shell.formatUrl({ type: "dm", ws: ws.slug, userId }))}
        onChannelPress={(id) => nav.open(Shell.formatUrl({ type: "channel", ws: ws.slug, channelId: id }))}
        onGroupPress={groupCard === null ? undefined : (id) => groupCard.open(id)}
        fileActions={fileActions}
        uploadProgress={s.sendProgress[item.key]}
        onStickerPress={stickers === null ? undefined : (packId) => stickers.vm.openPack(packId)}
        onEdit={th.vm.canEdit(m.id) ? () => th.vm.startEdit(m.id) : undefined}
        onDelete={th.vm.canDelete(m.id) ? () => th.vm.askDelete(m.id) : undefined}
        extras={extrasFor(m)}
        editor={
          s.editing !== undefined && s.editing.messageId === m.id ? (
            <Composer
              value={s.editing.draft}
              chips={ctx}
              onChange={(text) => th.vm.setEditDraft(text)}
              onSend={() => th.vm.saveEdit()}
              onCancel={() => th.vm.cancelEdit()}
              canSend={s.editing.canSave}
              placeholder={t("message.edit")}
              autoFocus
              autocomplete={s.editing.autocomplete}
              selection={s.editing.selection}
              onSelectionChange={s.composerAutocomplete ? (start, end) => th.vm.setEditSelection(start, end) : undefined}
              onAutocompleteKey={(key) => th.vm.editKey(key)}
              onAutocompleteChoose={(i) => th.vm.chooseEditAutocomplete(i)}
              onAutocompleteHover={(i) => th.vm.setEditAutocompleteActive(i)}
              onAutocompleteDismiss={() => th.vm.dismissEditAutocomplete()}
              testID="thread-edit-composer"
            />
          ) : undefined
        }
        alsoSentLabel={s.alsoSentLabel === undefined ? undefined : tr(s.alsoSentLabel)}
        follow={s.follows === undefined || m.id !== rootId ? undefined : { following: th.vm.following(m.id) === true, onToggle: () => th.vm.setFollow(m.id, th.vm.following(m.id) !== true) }}
      />
    );
    if (m.id !== rootId) return withEphemerals(row);
    return withEphemerals(
      <Stack>
        {row}
        <ThreadSummaryCard
          card={summary.state.card}
          onSummarize={() => void summary.vm.summarize()}
          onToggleCollapsed={() => summary.vm.toggleCollapsed()}
          testID="thread-summary"
        />
        <Stack direction="horizontal" align="center" gap="3" paddingX="5" paddingY="2">
          <Text variant="caption" weight="semibold" color="text.tertiary">
            {t("message.replies", { count: m.replyCount })}
          </Text>
          <Stack grow>
            <Divider />
          </Stack>
        </Stack>
      </Stack>,
    );
  };

  return (
    <DropZone
      onFiles={(files) => th.vm.addFiles(files)}
      label={tr(s.dropLabel)}
      sublabel={t("files.drop.subtitle", { max: Files.MAX_FILES_PER_MESSAGE })}
      disabled={!s.isMember}
      testID="thread-dropzone"
    >
    <Stack grow testID="thread-screen">
      <Stack direction="horizontal" align="center" gap="2" paddingX="5" paddingY="2.5">
        <Icon name="thread" size="icon.md" color="text.secondary" />
        <Text variant="headline" weight="semibold">
          {t("thread.title")}
        </Text>
        <Stack grow>
          <Text variant="footnote" color="text.secondary" lines={1}>
            {s.threadIn === undefined ? "" : tr(s.threadIn)}
          </Text>
        </Stack>
        {summary.state.card.kind === "hidden" ? null : (
          <IconButton
            icon="sparkles"
            accessibilityLabel={t(summary.state.card.kind === "ready" ? "summary.refresh" : "summary.summarize")}
            onPress={() => void summary.vm.summarize()}
            disabled={summary.state.card.loading}
            testID="thread-summarize"
          />
        )}
        {s.follows === undefined ? null : (
          <IconButton
            icon={th.vm.following(rootId) === true ? "bell" : "bellOff"}
            variant={th.vm.following(rootId) === true ? "filled" : "ghost"}
            accessibilityLabel={t(th.vm.following(rootId) === true ? "thread.follow.off" : "thread.follow.on")}
            onPress={() => th.vm.setFollow(rootId, th.vm.following(rootId) !== true)}
            testID="thread-follow"
          />
        )}
        <IconButton icon="close" accessibilityLabel={t("common.close")} onPress={nav.close} testID="thread-close" />
      </Stack>
      <Divider />
      <List
        items={s.items}
        keyOf={(i) => i.key}
        renderItem={renderItem}
        inverted
        scrollTo={scrollTo}
        empty={
          <Stack grow align="center" justify="center" padding="8">
            <Text color="text.tertiary">{t("common.loading")}</Text>
          </Stack>
        }
        testID="thread-timeline"
      />
      <Stack gap="1" paddingX="5" paddingY="3">
        {s.runningCommand === undefined ? null : <CommandRunningRow command={s.runningCommand} testID="thread-composer-command-running" />}
        <ScheduledBar channelId={channelId} threadRootId={rootId} onSeeAll={() => nav.open(Shell.formatUrl({ type: "scheduled", ws: ws.slug }))} testID="thread-composer-scheduled" />
        {s.blockedPeer !== undefined ? (
          <BlockedComposer userId={s.blockedPeer.userId} name={s.blockedPeer.name} testID="thread-composer-blocked" />
        ) : billing?.state.view?.readOnly === true ? (
          <BillingComposerNotice vm={billing.vm} state={billing.state} testID="thread-composer-billing-read-only" />
        ) : access("chat") !== "full" ? (
          <Text variant="footnote" color="text.secondary" testID="thread-composer-feature-read-only">
            {t("features.readOnly", { area: t(FEATURE_COPY.chat.title) })}
          </Text>
        ) : (
        <Composer
          value={s.draft}
          chips={ctx}
          onChange={(text) => th.vm.setDraft(text)}
          onSend={() => {
            th.vm.send();
            setScrollTo({ key: "", seq: (scrollTo?.seq ?? 0) + 1 });
          }}
          placeholder={tr(s.composerPlaceholder)}
          disabled={!s.isMember}
          autoFocus={nav.focused}
          attachments={s.attachments}
          onAddFiles={(files) => th.vm.addFiles(files)}
          onRemoveAttachment={(id) => th.vm.removeAttachment(id)}
          onRetryAttachment={(id) => th.vm.retryAttachment(id)}
          canSend={s.canSend}
          autocomplete={s.autocomplete}
          commandHint={s.commandHint}
          selection={s.composerSelection}
          onSelectionChange={s.composerAutocomplete ? (start, end) => th.vm.setComposerSelection(start, end) : undefined}
          onAutocompleteKey={(key) => th.vm.composerKey(key)}
          onAutocompleteChoose={(i) => th.vm.chooseAutocomplete(i)}
          onAutocompleteHover={(i) => th.vm.setAutocompleteActive(i)}
          onAutocompleteDismiss={() => th.vm.dismissAutocomplete()}
          onMention={s.composerAutocomplete ? () => th.vm.openMention() : undefined}
          renderMediaPicker={
            picker === null
              ? undefined
              : (insert) => (
                  <MediaPicker
                    vm={picker.vm}
                    state={picker.state}
                    fill={!nav.canSplit}
                    onSent={() => setScrollTo({ key: "", seq: (scrollTo?.seq ?? 0) + 1 })}
                    onEmoji={(e) => {
                      insert(e);
                      picker.vm.close();
                    }}
                    onCreateSticker={
                      stickers?.state.canCreate === true
                        ? () => {
                            picker.vm.close();
                            stickers.creator.open();
                          }
                        : undefined
                    }
                    onManageStickers={() => {
                      picker.vm.close();
                      nav.open(Shell.formatUrl({ type: "settings", ws: ws.slug, section: "stickers" }));
                    }}
                    testID="thread-composer-picker"
                  />
                )
          }
          mediaPickerOpen={picker?.state.open}
          onMediaPickerOpenChange={(open) => (open ? picker?.vm.open() : picker?.vm.close())}
          mediaPickerSheet={!nav.canSplit}
          mediaPickerTitle={t("picker.title")}
          alsoSendLabel={s.alsoSend === undefined ? undefined : tr(s.alsoSend.label)}
          alsoSend={s.alsoSend?.checked}
          onAlsoSendChange={(checked) => th.vm.setAlsoSend(checked)}
          sendMenu={schedule.sendMenu}
          testID="thread-composer"
        />
        )}
        <TypingIndicator label={s.typing} />
      </Stack>
      <Toast
        open={s.attachNotice !== undefined}
        onClose={() => th.vm.dismissAttachNotice()}
        message={s.attachNotice === undefined ? "" : tr(s.attachNotice)}
        closeLabel={t("common.close")}
        durationMs={4000}
        testID="thread-attach-notice"
      />
      {schedule.sheet}
      <CommandNoticeToast vm={th.vm} notice={s.commandNotice} />
      <DeleteMessageDialog confirm={s.deleting} onConfirm={() => th.vm.confirmDelete()} onCancel={() => th.vm.cancelDelete()} testID="thread-delete-message" />
      <BroadcastConfirmDialog confirm={s.broadcastConfirm} onConfirm={() => th.vm.confirmBroadcast()} onCancel={() => th.vm.cancelBroadcast()} testID="thread-broadcast-confirm" />
    </Stack>
    </DropZone>
  );
}
